import { Component, computed, DestroyRef, inject, Input } from '@angular/core';
import { HeaderComponent } from '@coreui/angular';
import { AuthService } from '@auth0/auth0-angular';
import { SessionService } from '../../../services/session/session.service';
import { UsersApiService } from '../../../services/session/users-api.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, distinctUntilChanged, filter, of, switchMap } from 'rxjs';
import { Organization, Role } from '../../../services/session/user.model';
import { PermissionsService } from '../../../core/permissions/permissions.service';
import { ThemeService, Theme } from '../../../services/theme/theme.service';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-default-header',
    templateUrl: './default-header.component.html',
    styleUrls: ['./default-header.component.scss'],
})
export class DefaultHeaderComponent extends HeaderComponent {
    @Input() sidebarId = 'sidebar';

    user            = toSignal(this.auth.user$);
    originalRole    = toSignal(this.sessionService.originalRole$);
    userRole        = toSignal(this.sessionService.assumedRole$);
    roleLabel       = computed(() => this.userApiService.roleToLabel(this.userRole() || ''));
    accountOrg      = toSignal(this.sessionService.accountOrg$);

    // ── Permission-driven flags ───────────────────────────────────────────────
    canSwitchRole     = computed(() => this.permissions.can('read', 'SwitchRole'));
    canSeeOrgSwitcher = computed(() => this.permissions.can('read', 'OrgSwitcher'));
    canChangeRole     = toSignal(this.sessionService.isRolePickerNeeded$, { initialValue: false });
    canSwitchAccount  = computed(() => {
        if (this.originalRole() === 'ROOT_ADMIN') return true;
        if (this.isOrgLevel()) {
            const uniqueGroups = new Set(this.allOrganizations().map(o => o.account_id));
            return uniqueGroups.size > 1;
        }
        return false;
    });

    // Derived context helpers (kept for the "Switch account / org" label logic)
    isAccountAdmin  = computed(() => this.userRole() === 'SYSTEM_ADMIN' || this.userRole() === 'ACCOUNT_ADMIN');
    isOrgLevel      = computed(() => this.userRole() === 'ORG_ADMIN'    || this.userRole() === 'ORG_MEMBER');

    private allOrganizations = toSignal(
        this.sessionService.assumedRole$.pipe(
            filter(role => !!role),
            distinctUntilChanged(),
            switchMap(() =>
                this.permissions.can('read', 'OrgSwitcher')
                    ? this.userApiService.getOrganizations$().pipe(catchError(() => of([])))
                    : of([]),
            ),
        ),
        { initialValue: [] as Organization[] },
    );

    organizations = computed(() => {
        const groupId = this.accountOrg()?.account?.id;
        const orgs    = this.allOrganizations();
        return groupId ? orgs.filter(org => org.account_id === groupId) : orgs;
    });

    readonly assumableRoles: Role[] = ['ORG_ADMIN', 'ORG_MEMBER', 'ACCOUNT_ADMIN', 'ROOT_ADMIN'];
    originalRoleLabel = computed(() => this.userApiService.roleToLabel(this.originalRole() ?? ''));

    private readonly themeService = inject(ThemeService);
    readonly showThemeSelector = environment.showThemeSelector;
    readonly currentTheme = this.themeService.theme;
    readonly themeOptions: { value: Theme; label: string; icon: string }[] = [
        { value: 'light', label: 'Light', icon: 'cilSun' },
        { value: 'dark',  label: 'Dark',  icon: 'cilMoon' },
        { value: 'auto',  label: 'System', icon: 'cilContrast' },
    ];

    setTheme(theme: Theme) {
        this.themeService.setTheme(theme);
    }

    constructor(
        private auth: AuthService,
        private sessionService: SessionService,
        readonly userApiService: UsersApiService,
        private router: Router,
        private destroyRef: DestroyRef,
        private permissions: PermissionsService,
    ) {
        super();
    }

    switchOrg(org: Organization) {
        this.sessionService
            .setAccountOrg({ id: org.account_id, name: org.account_name }, org)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                const current = this.router.url.split('?')[0].split(';')[0];
                // If already on an org-scoped URL, keep the same sub-path under the new org.
                // Non-org paths fall back to /dashboard.
                const isOrgScoped = /^\/orgs\/[^/]+\//.test(current);
                const newUrl = isOrgScoped
                    ? current.replace(/^\/orgs\/[^/]+/, `/orgs/${org.org_id}`)
                    : `/orgs/${org.org_id}/dashboard`;
                // Navigate via blank route first so Angular destroys and recreates all
                // child components, ensuring they fetch fresh data for the new org.
                this.router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
                    this.router.navigateByUrl(newUrl)
                );
            });
    }

    logout() {
        this.sessionService.clearAccountOrg();
        this.auth.logout({ logoutParams: { returnTo: document.location.origin + '/login' } });
    }

    switchAccount() {
        this.sessionService.clearAccountOrg();
        this.router.navigate(['/'], { state: { skipRolePicker: true } });
    }

    assumeRoleFromHeader(role: Role) {
        const current = this.accountOrg();
        const hasOrgCtx    = !!current?.organization;
        const hasAccountCtx = !!current?.account;
        const orgLevelRoles: Role[]     = ['ORG_ADMIN', 'ORG_MEMBER', 'ROOT_ADMIN'];
        const accountLevelRoles: Role[] = ['ACCOUNT_ADMIN', 'SYSTEM_ADMIN'];

        // Switching between org-level roles while in an org — stay on the page
        if (hasOrgCtx && orgLevelRoles.includes(role) && orgLevelRoles.includes(this.userRole()!)) {
            this.sessionService.roleOverride$.next(role === 'ROOT_ADMIN' ? '' : role);
            return;
        }

        // Switching to an account-level role while in any account context — keep the account
        if (hasAccountCtx && accountLevelRoles.includes(role)) {
            const account = current.account!;
            this.sessionService.clearAccountOrg();
            this.sessionService.roleOverride$.next(role);
            this.sessionService.setAccountOrg(account).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
            this.router.navigate(['/accounts', account.id, 'organizations']);
            return;
        }

        // Switching to an org-level role while in an account context — keep the account, skip to org selection
        if (hasAccountCtx && orgLevelRoles.includes(role)) {
            const account = current.account!;
            this.sessionService.clearAccountOrg();
            this.sessionService.roleOverride$.next(role === 'ROOT_ADMIN' ? '' : role);
            this.router.navigate(['/'], { state: { skipRolePicker: true, preselectedAccount: account } });
            return;
        }

        // Default: no account context — clear and let the user re-select
        this.sessionService.clearAccountOrg();
        this.sessionService.roleOverride$.next(role === 'ROOT_ADMIN' ? '' : role);
        this.router.navigate(['/'], { state: { skipRolePicker: true } });
    }

    loseRole() {
        this.sessionService.clearAccountOrg();
        this.sessionService.roleOverride$.next('');
        this.router.navigate(['/']);
    }
}
